import React from 'react';
import I18n from 'i18n-js';
import gql from 'graphql-tag';
import { graphql, MutationFn } from 'react-apollo';
import { CoreShopSettingsCreateTaxIdentification } from '../schemas/rql';
import FormGroupWithInput from '@reverbdotcom/commons/src/components/form_group_with_input';
import FormGroupWithSelect, { ISelectOption } from '@reverbdotcom/commons/src/components/form_group_with_select';
import ModalDialog from '@reverbdotcom/commons/src/components/modal_dialog';
import ShopSettingsVatIdSaveError from './shop_settings_vat_id_save_error';
import vatIdHelpText from './shop_settings_vat_id_help_text';

interface ExternalProps {
  countryOptions: ISelectOption[];
  isOpen: boolean;
  closeForm: () => void;
}

interface MutationProps {
  mutate: MutationFn<
  CoreShopSettingsCreateTaxIdentification.Mutation,
  CoreShopSettingsCreateTaxIdentification.Variables
  >;
}

type Props = ExternalProps & MutationProps;

export function ShopSettingsCreateVatIdForm(props: Props) {
  const {
    countryOptions,
    isOpen,
    closeForm,
    mutate,
  } = props;

  const [countryCode, setCountryCode] = React.useState('');
  const [taxId, setTaxId] = React.useState('');
  const [error, setError] = React.useState('');

  async function createTaxIdentification() {
    try {
      const response = await mutate({
        variables: {
          input: {
            taxId,
            countryCode,
          },
        },
      });

      if (response) {
        clearAndCloseForm();
      }
    } catch (e) {
      setError(I18n.t('discovery.shopSettings.vat.saveFailed'));
    }
  }

  function clearAndCloseForm() {
    setTaxId('');
    setCountryCode('');
    closeForm();
  }

  return (
    <ModalDialog
      isOpen={isOpen}
      onRequestClose={closeForm}
      headerTitle={I18n.t('discovery.shopSettings.vat.addVat')}
      saveButtonText={I18n.t('discovery.shopSettings.vat.save')}
      onSubmit={createTaxIdentification}
    >
      <ShopSettingsVatIdSaveError
        countryCode={countryCode}
        error={error}
      />
      <FormGroupWithSelect
        inputName="countryCode"
        fieldName="countryCode"
        label={I18n.t('discovery.shopSettings.vat.countryCode')}
        value={countryCode}
        updateField={(field) => setCountryCode(field.countryCode)}
        options={countryOptions}
        required
        addEmptyOption
        helpText={I18n.t('discovery.shopSettings.vat.countryAlreadyUsed')}
      />
      <FormGroupWithInput
        inputName="taxId"
        fieldName="taxId"
        inputType="text"
        label={I18n.t('discovery.shopSettings.vat.vatId')}
        value={taxId}
        updateField={(field) => setTaxId(field.taxId)}
        pattern="[a-zA-Z0-9]+"
        helpText={vatIdHelpText(countryCode, countryOptions)}
        required
      />
    </ModalDialog>
  );
}

const CREATE_TAX_IDENTIFICATION_MUTATION = gql`
  mutation Core_ShopSettings_CreateTaxIdentification(
    $input: Input_core_apimessages_CreateTaxIdentificationRequest
  ) {
    createTaxIdentification(input: $input) {
      shop {
        _id
        taxIdentifications {
          id
          taxId
          taxType
          status
          countryCode
        }
      }
    }
  },
`;

const withMutation = graphql<
ExternalProps,
CoreShopSettingsCreateTaxIdentification.Mutation,
CoreShopSettingsCreateTaxIdentification.Variables
>(
  CREATE_TAX_IDENTIFICATION_MUTATION,
);

export default withMutation(ShopSettingsCreateVatIdForm);
